/**
 * @file date-range.component.tsx
 * @description Компонент работы с диапазоном дат. Используется библиотека @dhx/suite для календаря и выбора диапазона дат
 * @author AI-Generated | Human-Reviewed
 * @created 2025-09-03
 * @updated 2025-09-17
 * @dependencies ['react-imask', '@dhx/suite', '@components/ui/base/text-input', '@consta/icons/IconCalendar', 'moment', 'classnames', '@consta/uikit/useGlobalKeys', '@src/utils/date-time-helper']
 * @related-files ['src/components/ui/base/text-input/text-input.component.tsx']
 */

import { useIMask, IMask, ReactMaskOpts } from 'react-imask';
import { useEffect, useRef, useMemo } from 'react';
import * as dhx from '@src/modules/dhx-suite/codebase/suite.js';
const { Calendar, Popup, i18n } = dhx;
import '@src/modules/dhx-suite/codebase/suite.min.css';
import { TextInput } from '@components/ui/base/text-input';
import { IconCalendar } from '@consta/icons/IconCalendar';
import moment from 'moment';
import cn from 'classnames';
import { useGlobalKeys } from '@consta/uikit/useGlobalKeys';
import { stringToTimestamp } from '@src/utils/date-time-helper';

/**
 * Props для компонента выбора диапазона дат
 */
interface IDateRangePickerProps {
    /** Текст метки для поля ввода диапазона дат */
    label?: string;
    /** Значение диапазона дат в формате [начальная_дата, конечная_дата] в unix timestamp */
    value: [number | null, number | null];
    /** Обработчик изменения диапазона дат */
    onChange?: ((value: [number | null, number | null]) => void) | undefined;
    /** Режим только для чтения */
    readOnly?: boolean;
    /** Функция добавления модального окна в очередь */
    addModal: () => void;
    /** Уникальный идентификатор модального окна */
    modalId: string;
    /** Текущая очередь модальных окон */
    currentModalsQueue: string[];
    /** Функция удаления модального окна из очереди по ID */
    removeModalFromQueueById: () => void;
    /** CSS класс для дополнительной стилизации */
    className?: string;
    /** Обязательное поле */
    required?: boolean;
}

export const DateRangePicker = ({
    label,
    value,
    onChange,
    readOnly = Boolean(!onChange),
    addModal,
    modalId,
    currentModalsQueue,
    removeModalFromQueueById,
    className,
    required = false,
    ...props
}: IDateRangePickerProps) => {
    i18n.setLocale('calendar', {
        monthsShort: moment.monthsShort(),
        months: moment.months(),
        daysShort: moment.weekdaysShort(),
        days: moment.weekdays(),
        cancel: 'Отмена'
    });
    i18n.setLocale('timepicker', {
        hours: 'Часы',
        minutes: 'Минуты',
        save: 'Сохранить'
    });

    const momentFormat = useMemo<string>(() => 'DD.MM.YYYY', []);
    // const initialValue = useMemo<string[]>(() => {
    //     let startDate: any = value[0] ? moment(value[0]) : null;
    //     let endDate: any = value[1] ? moment(value[1]) : null;

    //     startDate = startDate?.year() > 1970 ? startDate.format(momentFormat) : null;
    //     endDate = endDate?.year() > 1970 ? endDate.format(momentFormat) : null;

    //     return [startDate, endDate];
    // }, [_value]);

    const datePickerValue = useMemo(() => {
        return [
            value[0] && value[0] > 0 ? moment(value[0]).format(momentFormat) : '',
            value[1] && value[1] > 0 ? moment(value[1]).format(momentFormat) : ''
        ];
    }, [value]);

    const containerRef = useRef(null);
    const popup = useRef(new Popup());
    const itemsRef = useRef<string[] | null>(null);

    const {
        ref: startRef,
        value: startValue,
        setValue: setStartValue
    } = useIMask<HTMLInputElement, ReactMaskOpts>(iMaskProps);
    const {
        ref: endRef,
        value: endValue,
        setValue: setEndValue
    } = useIMask<HTMLInputElement, ReactMaskOpts>(iMaskProps);

    const startCalendar = useRef(
        new Calendar('', {
            dateFormat: '%d.%m.%Y',
            weekStart: 'monday',
            value: datePickerValue[0],
            date: datePickerValue[0]
            // mark: (date) => {
            //     if (moment(date).format('L') === moment().format('L')) {
            //         return 'dhx_calendar-day--current-date';
            //     }
            // }
        })
    );
    const endCalendar = useRef(
        new Calendar('', {
            dateFormat: '%d.%m.%Y',
            weekStart: 'monday',
            value: datePickerValue[1],
            date: datePickerValue[1]
            // mark: (date) => {
            //     if (moment(date).format('L') === moment().format('L')) {
            //         return 'dhx_calendar-day--current-date';
            //     }
            // }
        })
    );

    const changeValueHandler = (startVal: string | null, endVal: string | null) => {
        onChange &&
            onChange([
                stringToTimestamp(startVal, momentFormat),
                stringToTimestamp(endVal, momentFormat)
            ]);
    };

    const showStartCalendarHandler = () => {
        startRef.current?.select();
        popup.current.attach(startCalendar.current);
        if (containerRef.current) {
            popup.current.show(containerRef.current);
        }
    };

    const showEndCalendarHandler = () => {
        popup.current.attach(endCalendar.current);
        if (containerRef.current) {
            popup.current.show(containerRef.current);
        }
        endRef.current?.select();
    };

    const changeStartValueHandler = (newValue: string | null | undefined) => {
        if (!newValue) {
            startCalendar.current.clear();
            const endVal = endCalendar.current.getValue();
            const endStringValue = endVal instanceof Date ? moment(endVal).format(momentFormat) : endVal;
            changeValueHandler(null, endStringValue);
            return;
        }

        if (moment(newValue, momentFormat, true).isValid()) {
            startCalendar.current.setValue(newValue);
            const endVal = endCalendar.current.getValue();
            const endStringValue = endVal instanceof Date ? moment(endVal).format(momentFormat) : endVal;
            changeValueHandler(newValue, endStringValue);
            endRef.current?.focus();
        }

        setStartValue(newValue);
    };

    const changeEndValueHandler = (newValue: string | null | undefined) => {
        if (!newValue) {
            endCalendar.current.clear();
            const startVal = startCalendar.current.getValue();
            const startStringValue = startVal instanceof Date ? moment(startVal).format(momentFormat) : startVal;
            changeValueHandler(startStringValue, null);
            return;
        }

        if (moment(newValue, momentFormat, true).isValid()) {
            endCalendar.current.setValue(newValue);
            const startVal = startCalendar.current.getValue();
            const startStringValue = startVal instanceof Date ? moment(startVal).format(momentFormat) : startVal;
            changeValueHandler(startStringValue, newValue);
            popup.current.hide();
        }

        setEndValue(newValue);
    };

    startCalendar.current.link(endCalendar.current);

    useEffect(() => {
        if (datePickerValue[0]) {
            setStartValue(datePickerValue[0]);
            startCalendar.current.setValue(datePickerValue[0]);
        }

        if (datePickerValue[1]) {
            setEndValue(datePickerValue[1]);
            endCalendar.current.setValue(datePickerValue[1]);
        }

        startCalendar.current.events.on('change', (_date: Date, _oldDate: Date, byClick: boolean) => {
            if (byClick) {
                popup.current.hide();
            }

            const startVal = startCalendar.current?.getValue();
            const startStringValue = startVal instanceof Date ? moment(startVal).format(momentFormat) : startVal;
            setStartValue(startStringValue);

            if (moment(startCalendar.current.getValue(), 'L') > moment(endCalendar.current.getValue(), 'L')) {
                const newEndValue = startCalendar.current.getValue();
                const newEndStringValue = newEndValue instanceof Date ? moment(newEndValue).format(momentFormat) : newEndValue;
                setEndValue(newEndStringValue);
                endCalendar.current.setValue(newEndValue);
            }

            if (byClick) {
                const startVal = startCalendar.current.getValue();
                const endVal = endCalendar.current.getValue();
                const startStringValue = startVal instanceof Date ? moment(startVal).format(momentFormat) : startVal;
                const endStringValue = endVal instanceof Date ? moment(endVal).format(momentFormat) : endVal;
                changeValueHandler(startStringValue, endStringValue);
                showEndCalendarHandler();
            }
        });

        endCalendar.current.events.on('change', (_date: Date, _oldDate: Date, byClick: boolean) => {
            if (byClick) {
                popup.current.hide();
            }

            const endVal = endCalendar.current?.getValue();
            const endStringValue = endVal instanceof Date ? moment(endVal).format(momentFormat) : endVal;
            setEndValue(endStringValue);

            if (moment(endCalendar.current.getValue(), 'L') < moment(startCalendar.current.getValue(), 'L')) {
                const newStartValue = endCalendar.current.getValue();
                const newStartStringValue = newStartValue instanceof Date ? moment(newStartValue).format(momentFormat) : newStartValue;
                setStartValue(newStartStringValue);
                startCalendar.current.setValue(newStartValue);

                showStartCalendarHandler();
            }

            if (byClick) {
                const startVal = startCalendar.current.getValue();
                const endVal = endCalendar.current.getValue();
                const startStringValue = startVal instanceof Date ? moment(startVal).format(momentFormat) : startVal;
                const endStringValue = endVal instanceof Date ? moment(endVal).format(momentFormat) : endVal;
                changeValueHandler(startStringValue, endStringValue);
            }
        });

        popup.current.events.on('afterShow', function () {
            addModal();
            return true;
        });

        popup.current.events.on('afterHide', function () {
            removeModalFromQueueById();
            startRef.current?.blur();
            endRef.current?.blur();
            return true;
        });
    }, []);

    // useEffect(() => {
    //     datePickerValue[0] ? startCalendar.current.setValue(datePickerValue[0]) : startCalendar.current.clear();
    //     datePickerValue[1] ? endCalendar.current.setValue(datePickerValue[1]) : endCalendar.current.clear();
    // }, [datePickerValue]);

    useEffect(() => {
        itemsRef.current = currentModalsQueue;
    }, [currentModalsQueue]);

    useGlobalKeys({
        Escape: () => {
            if (itemsRef.current?.at(-1) === modalId) {
                popup.current.hide();
            }
        }
    });

    return (
        <div ref={containerRef} className={cn('flex items-end', className)}>
            <TextInput
                size="s"
                iconSize="xs"
                form="defaultBrick"
                label={label}
                inputRef={startRef}
                readOnly={readOnly}
                rightSide={IconCalendar}
                onFocus={() => (onChange ? showStartCalendarHandler() : null)}
                onChange={changeStartValueHandler}
                {...props}
                className={cn('DatePickerXMes', { 'pointer-events-none': readOnly, 'cursor-pointer': !readOnly })}
                required={Boolean(label && required)}
                value={startValue}
            />

            <TextInput
                size="s"
                iconSize="xs"
                form="clearDefault"
                value={endValue}
                inputRef={endRef}
                readOnly={readOnly}
                rightSide={IconCalendar}
                onFocus={() => (onChange ? showEndCalendarHandler() : null)}
                onChange={changeEndValueHandler}
                className={cn('DatePickerXMes', { 'pointer-events-none': readOnly, 'cursor-pointer': !readOnly })}
                {...props}
                label={label ? ' ' : undefined}
                required={false}
            />
        </div>
    );
};

const iMaskProps = {
    mask: Date,
    pattern: 'DD.MM.YYYY',
    format: (date: Date | null) => (date ? moment(date).format('DD.MM.YYYY') : ''),
    parse: (str: string) => moment(str, 'DD.MM.YYYY').toDate(),
    min: new Date(1970, 0, 1),
    max: new Date(2030, 0, 1),
    blocks: {
        YYYY: {
            mask: IMask.MaskedRange,
            from: 1970,
            to: 2030
        },
        MM: {
            mask: IMask.MaskedRange,
            from: 1,
            to: 12
        },
        DD: {
            mask: IMask.MaskedRange,
            from: 1,
            to: 31
        }
    }
};
